import { useEffect, useState } from 'react'
import type { IndexDef, LevelCode, ObjectiveState } from '@/api/types/readiness'
import { LEVELS, levelNum } from '@/api/types/readiness'
import { levelName, tiersOf, tierForScore } from '@/lib/indexes/registry'
import { maxReachable, needsConfirmation, needsScoreConfirmation, reachable, tierScore } from '@/lib/readiness/score'
import { fmtScore } from '@/lib/readiness/scale'
import { Icon, SectionLabel } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'

const FOOTNOTE = 'Confirmed levels are stamped with your name and date, and appear in assessments and the CSA export. Auto-sync flags, never overwrites, a confirmed level. A level can be moved down with a note, for example after a vendor or organisational change.'

// Hosted inside the quick-view drawer (I6 POC §7.3): the panel is always the
// editable card, `Cancel` folds it away, and `Set` stays disabled until the
// selection differs from the current confirmed level.
export interface EmbeddedPanel {
  onCancel: () => void
}

// Right-rail confirm panel (I1 §8.5, I2 §6): a user may set a level lower than
// the current one; a note is required, as for any override.
// previousLevel is the objective's level in the previous assessment: levels
// above the next one up are out of reach.
export function ConfirmPanel({ index, state, evidenceCount, color, onConfirm, embedded, requireNote, previousLevel }: { index: IndexDef; state: ObjectiveState | undefined; evidenceCount: number; color: string; onConfirm: (level: LevelCode, note: string, cause?: 'coverage' | 'posture') => void; embedded?: EmbeddedPanel; requireNote?: boolean; previousLevel?: LevelCode | null }) {
  const pending = needsConfirmation(state)
  const max = maxReachable(previousLevel)
  const clamp = (l: LevelCode): LevelCode => (reachable(l, previousLevel) ? l : max!)
  const rawSuggested = state?.suggested_level ?? null
  const suggested = rawSuggested == null ? null : clamp(rawSuggested)
  const isEmbedded = !!embedded
  const [editing, setEditing] = useState(isEmbedded || pending || !state || state.status === 'unset')
  const [level, setLevel] = useState<LevelCode>(clamp(suggested ?? state?.level ?? 'L0'))
  const [note, setNote] = useState('')
  const [coverage, setCoverage] = useState(false)
  useEffect(() => {
    setEditing(isEmbedded || pending || !state || state.status === 'unset')
    setLevel(clamp(suggested ?? state?.level ?? 'L0'))
    setNote('')
    setCoverage(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clamp follows previousLevel
  }, [isEmbedded, pending, suggested, state, previousLevel])

  const overriding = suggested != null && level !== suggested
  const confirmed = state && (state.status === 'confirmed' || state.status === 'declared')
  const downward = !!confirmed && levelNum(level) < levelNum(state!.level)
  const noteRequired = !!requireNote || overriding || (suggested == null && state?.status !== 'unset' && level !== state?.level) || downward
  const unchanged = !!embedded && !!confirmed && !pending && level === state!.level
  const canSubmit = (!noteRequired || note.trim().length > 0) && !unchanged

  if (!editing && confirmed) {
    return (
      <section className="rd-card p-5" aria-labelledby="rd-confirm-title">
        <SectionLabel>Level confirmed</SectionLabel>
        <p id="rd-confirm-title" className="mt-2 text-[14px] font-semibold">
          {state.level} · by {state.set_by} · {formatDate(state.set_at)}
        </p>
        {state.note && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>"{state.note}"</p>}
        <button type="button" className="rd-link mt-3 text-[14px]" onClick={() => setEditing(true)}>Change level</button>
        <p className="mt-3 text-[11.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)' }}>{FOOTNOTE}</p>
      </section>
    )
  }

  return (
    <section className="rd-card p-5" style={pending ? { border: '1px solid var(--rd-status-warn)' } : undefined} aria-labelledby="rd-confirm-title">
      {pending ? (
        <>
          <p id="rd-confirm-title" className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: 'var(--rd-status-warn)' }}>
            <Icon name="warn" size={16} /> Needs your confirmation
          </p>
          <p className="mt-2 text-[14px]" style={{ color: 'var(--rd-text-secondary)' }}>
            Quantizant suggests <b>{suggested} · {levelName(index, suggested!)}</b> based on {evidenceCount} evidence {evidenceCount === 1 ? 'item' : 'items'}. Confirm, or set a different level with a reason.
          </p>
        </>
      ) : (
        <>
          <SectionLabel>Set level</SectionLabel>
          <p id="rd-confirm-title" className="mt-2 text-[14px]" style={{ color: 'var(--rd-text-secondary)' }}>
            {state?.status === 'unset' || !state ? 'No system suggestion for this objective. Declare the level that the evidence supports.' : 'Choose a new level, up or down. A note is required when changing a confirmed level.'}
          </p>
        </>
      )}
      <p className="rd-label mt-4">Set level</p>
      <div className="mt-1.5 grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Set level">
        {LEVELS.map((l) => {
          const on = l === level
          const ok = reachable(l, previousLevel)
          return (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!ok}
              title={ok ? undefined : `Levels progress one at a time: at most ${max} after ${previousLevel} in the previous assessment`}
              onClick={() => setLevel(l)}
              className="rd-btn !px-2 !py-2 disabled:opacity-40"
              style={on ? { background: `${color}22`, borderColor: color, color } : undefined}
              data-testid={`set-level-${l}`}
            >
              <span className="rd-mono">{l}</span>
            </button>
          )
        })}
      </div>
      <p className="mt-1 text-[11.5px]" style={{ color: downward ? 'var(--rd-status-warn)' : 'var(--rd-text-faint)' }}>{level} · {levelName(index, level)}{downward ? ` · ▼ down from ${state!.level}` : ''}</p>
      {max != null && max !== 'L3' && (
        <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }} data-testid="level-progression">
          Levels progress one at a time: {previousLevel} in the previous assessment, so at most {max} now.
          {rawSuggested != null && rawSuggested !== suggested ? ` The data supports ${rawSuggested}.` : ''}
        </p>
      )}
      {downward && (
        // Iteration 3 §4.1: the reason for a drop. Coverage-driven drops never lower the pace.
        <fieldset className="mt-3 text-[12.5px]" data-testid="drop-reason">
          <legend className="rd-label">Reason</legend>
          <label className="mt-1 flex items-center gap-2"><input type="radio" name="rd-drop-reason" checked={!coverage} onChange={() => setCoverage(false)} /> Something got worse (posture-driven)</label>
          <label className="mt-1 flex items-center gap-2"><input type="radio" name="rd-drop-reason" checked={coverage} onChange={() => setCoverage(true)} /> Coverage grew (more assets were found)</label>
          {coverage && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-coverage)' }}>More assets were found, so the level now describes a larger estate. This is better visibility, not regression.</p>}
        </fieldset>
      )}
      <label htmlFor="rd-confirm-note" className="rd-label mt-4 block">Note {noteRequired ? '(required)' : '(required if overriding)'}</label>
      <textarea id="rd-confirm-note" className="rd-input mt-1.5" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={downward ? 'Why the level moved down, e.g. a vendor or organisational change' : noteRequired ? 'Why this level differs from the suggestion' : 'Optional context for auditors'} aria-required={noteRequired} />
      <button
        type="button"
        className="rd-btn rd-btn-primary mt-3 w-full"
        disabled={!canSubmit}
        onClick={() => {
          onConfirm(level, note.trim(), downward ? (coverage ? 'coverage' : 'posture') : undefined)
          if (!embedded) setEditing(false)
        }}
        data-testid="confirm-level"
      >
        {overriding || suggested == null ? `Set ${level}` : `Confirm ${level}`}
      </button>
      {(confirmed || embedded) && (
        <button type="button" className="rd-link mt-2 w-full justify-center text-center text-[12.5px]" onClick={() => (embedded ? embedded.onCancel() : setEditing(false))} data-testid="confirm-cancel">Cancel</button>
      )}
      <p className="mt-3 text-[11.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)' }}>{FOOTNOTE}</p>
    </section>
  )
}

// Scale score_0_10 (I2 §11.2): pick a tier statement (score derived) or enter a
// different score with a note.
export function ScorePanel({ index, code, state, evidenceCount, color, onConfirm, embedded }: { index: IndexDef; code: string; state: ObjectiveState | undefined; evidenceCount: number; color: string; onConfirm: (input: { tier: number | null; score: number }, note: string) => void; embedded?: EmbeddedPanel }) {
  const tiers = tiersOf(index, code)
  const m = Math.max(2, tiers.length)
  const pending = needsScoreConfirmation(state)
  const suggestedScore = state?.suggested_score ?? null
  const suggestedTier = state?.suggested_tier ?? null
  const confirmed = state && (state.status === 'confirmed' || state.status === 'declared')
  const isEmbedded = !!embedded
  const [editing, setEditing] = useState(isEmbedded || pending || !state || state.status === 'unset')
  const [tier, setTier] = useState<number | null>(suggestedTier ?? state?.tier ?? null)
  const [score, setScore] = useState<string>(suggestedScore != null ? suggestedScore.toFixed(1) : state?.score != null ? state.score.toFixed(1) : '')
  const [note, setNote] = useState('')
  useEffect(() => {
    setEditing(isEmbedded || pending || !state || state.status === 'unset')
    setTier(suggestedTier ?? state?.tier ?? null)
    setScore(suggestedScore != null ? suggestedScore.toFixed(1) : state?.score != null ? state.score.toFixed(1) : '')
    setNote('')
  }, [isEmbedded, pending, suggestedScore, suggestedTier, state])
  const numeric = Number(score)
  const valid = score !== '' && !Number.isNaN(numeric) && numeric >= 0 && numeric <= 10
  const customScore = tier == null || Math.abs(tierScore(tier, m) - numeric) > 0.05
  const overriding = suggestedScore != null && Math.abs(numeric - suggestedScore) > 0.05
  const changing = !!confirmed && state?.score != null && Math.abs(numeric - state.score) > 0.05
  const noteRequired = overriding || customScore || changing
  const unchanged = !!embedded && !!confirmed && !pending && state?.score != null && Math.abs(numeric - state.score) <= 0.05
  const canSubmit = valid && (!noteRequired || note.trim().length > 0) && !unchanged
  const pickTier = (n: number) => {
    setTier(n)
    setScore(tierScore(n, m).toFixed(1))
  }
  if (!editing && confirmed) {
    const t = tierForScore(index, code, state!.score)
    return (
      <section className="rd-card p-5" aria-labelledby="rd-confirm-title">
        <SectionLabel>Score confirmed</SectionLabel>
        <p id="rd-confirm-title" className="mt-2 text-[14px] font-semibold">{fmtScore(state!.score)}{t ? ` · tier ${t.n} of ${m}` : ''} · by {state!.set_by} · {formatDate(state!.set_at)}</p>
        {state!.note && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>"{state!.note}"</p>}
        <button type="button" className="rd-link mt-3 text-[14px]" onClick={() => setEditing(true)}>Change score</button>
      </section>
    )
  }
  return (
    <section className="rd-card p-5" style={pending ? { border: '1px solid var(--rd-status-warn)' } : undefined} aria-labelledby="rd-confirm-title">
      {pending ? (
        <p id="rd-confirm-title" className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: 'var(--rd-status-warn)' }}><Icon name="warn" size={16} /> Needs your confirmation</p>
      ) : (
        <SectionLabel><span id="rd-confirm-title">Set score</span></SectionLabel>
      )}
      <p className="mt-2 text-[14px]" style={{ color: 'var(--rd-text-secondary)' }}>
        {pending ? <>Quantizant suggests <b>{fmtScore(suggestedScore)} · tier {suggestedTier} of {m}</b> based on {evidenceCount} evidence {evidenceCount === 1 ? 'item' : 'items'}. Confirm, or pick another tier with a reason.</> : 'Pick the tier statement that fits, or enter a different score with a note (for example your own HKMA survey result).'}
      </p>
      <div className="mt-3 space-y-1.5" role="radiogroup" aria-label="Tier">
        {tiers.map((t) => {
          const on = tier === t.n && !customScore
          return (
            <button key={t.n} type="button" role="radio" aria-checked={on} onClick={() => pickTier(t.n)} className="w-full rounded-[10px] p-2.5 text-left text-[12.5px] leading-relaxed" style={on ? { border: `2px solid ${color}`, background: `${color}14` } : { border: '1px solid var(--rd-border-control)', color: 'var(--rd-text-muted)' }}>
              <span className="rd-mono font-semibold" style={{ color: 'var(--rd-text-secondary)' }}>Tier {t.n} · {tierScore(t.n, m).toFixed(1)}</span> {t.statement}
            </button>
          )
        })}
      </div>
      <label htmlFor="rd-score-input" className="rd-label mt-4 block">Score (0–10)</label>
      <input id="rd-score-input" className="rd-input rd-mono mt-1.5" inputMode="decimal" value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 4.5" />
      <label htmlFor="rd-confirm-note" className="rd-label mt-4 block">Note {noteRequired ? '(required)' : '(optional)'}</label>
      <textarea id="rd-confirm-note" className="rd-input mt-1.5" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={customScore ? 'Where this score comes from, e.g. the HKMA survey result' : 'Optional context for auditors'} aria-required={noteRequired} />
      <button type="button" className="rd-btn rd-btn-primary mt-3 w-full" disabled={!canSubmit} onClick={() => { onConfirm({ tier: customScore ? null : tier, score: numeric }, note.trim()); if (!embedded) setEditing(false) }} data-testid="confirm-level">
        {overriding || suggestedScore == null ? `Set ${valid ? numeric.toFixed(1) : '—'}` : `Confirm ${numeric.toFixed(1)}`}
      </button>
      {(confirmed || embedded) && <button type="button" className="rd-link mt-2 w-full justify-center text-center text-[12.5px]" onClick={() => (embedded ? embedded.onCancel() : setEditing(false))} data-testid="confirm-cancel">Cancel</button>}
      <p className="mt-3 text-[11.5px] leading-relaxed" style={{ color: 'var(--rd-text-faint)' }}>Scores are stamped with your name and date and appear in assessments and the readiness report. The tier statements are Quantizant's reading of the report; the score is indicative, not a regulator result.</p>
    </section>
  )
}
